import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, Field, Modal, SelectField, StatusBadge } from "@fawkes/ui";

import { AiHulpKnop, useAiBeschikbaar } from "../../components/AiTariefHulp";

import { formatEuro } from "../../i18n/format";
import type { VestigingKeuze } from "../../components/useVestigingKeuze";
import { AccommodatieIcoon, type AccommodatieType } from "../pensionplanning/accommodatie";

/**
 * De dagprijzen van een accommodatietype, als kalender.
 *
 * <p>Hoort bij het prijsmodel "tarief per accommodatie": daar bepaalt de geboekte ruimte de prijs, en de
 * prijs van een dag mag per kalenderdag verschillen. Een dag zonder eigen prijs volgt de standaard uit de
 * prijsketen, en dat blijft zo als die standaard wijzigt — daarom staat er op zo'n dag geen kopie maar
 * een verwijzing, en toont de kalender het verschil.
 *
 * <p>Drie maanden tegelijk, want een vakantieperiode loopt zelden netjes binnen één maand.
 */

type T = (key: string, options?: { args?: unknown[] }) => string;

interface DagprijsDto {
  datum: string;
  bedrag: number | null;
  standaard: number | null;
  afwijkend: boolean;
}

/** De kleurtrappen voor afwijkende prijzen, van licht naar zwaar. */
const TRAPPEN = ["a", "b", "c", "d"] as const;

const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** De eerste van de maand, n maanden verschoven. */
const maandStart = (basis: Date, verschuiving: number): Date =>
  new Date(basis.getFullYear(), basis.getMonth() + verschuiving, 1);

interface AiVoorstel {
  van: string | null;
  tot: string | null;
  bedrag: number | null;
  volledig: boolean;
  uitleg: string;
}

/**
 * Hulp bij het vullen van de kalender.
 *
 * <p>Anders dan de hulp bij de tarieven zelf: die stelt een tarief voor, deze een periode met een
 * dagprijs. "Maak de kerstperiode 15% duurder" is wat iemand opschrijft; van-tot-bedrag is wat de
 * kalender nodig heeft.
 *
 * <p>Het voorstel wordt getoond en niet toegepast. Pas na "Toepassen" gebeurt er iets, en dan langs
 * dezelfde weg als wanneer je het zelf invult — zo is er geen tweede manier waarop een prijs ontstaat.
 */
function DagprijsAiVenster({ typeId, t, onSluit, onToegepast }: {
  typeId: number;
  t: T;
  onSluit: () => void;
  onToegepast: () => void;
}) {
  const [tekst, setTekst] = useState("");
  const [voorstel, setVoorstel] = useState<AiVoorstel | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const { mutate, isLoading: bezig } = useCustomMutation();

  const vraag = () => {
    if (tekst.trim() === "") return;
    setFout(null);
    mutate(
      {
        url: `pension-tarief/accommodaties/${typeId}/dagprijzen/ai-suggestie`,
        method: "post",
        values: { tekst: tekst.trim() },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => setVoorstel((res as { data: AiVoorstel }).data),
        onError: () => setFout(t("pensionTarief.dagprijs.ai.fout")),
      },
    );
  };

  const pasToe = () => {
    if (!voorstel?.volledig) return;
    setFout(null);
    mutate(
      {
        url: `pension-tarief/accommodaties/${typeId}/dagprijzen`,
        method: "put",
        values: { van: voorstel.van, tot: voorstel.tot, bedrag: voorstel.bedrag },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: onToegepast, onError: () => setFout(t("pensionTarief.dagprijs.ai.fout")) },
    );
  };

  return (
    <Modal open title={t("pensionTarief.dagprijs.ai.titel")} onClose={onSluit}
      footer={
        <>
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          <Button onClick={voorstel?.volledig ? pasToe : vraag} disabled={bezig || tekst.trim() === ""}>
            {voorstel?.volledig ? t("pensionTarief.dagprijs.ai.toepassen") : t("pensionTarief.dagprijs.ai.vraag")}
          </Button>
        </>
      }
    >
      <div className="ad-ai">
        <Field
          label={t("pensionTarief.dagprijs.ai.beschrijf")}
          value={tekst}
          placeholder={t("pensionTarief.dagprijs.ai.voorbeeld")}
          onChange={(e) => { setTekst(e.target.value); setVoorstel(null); }}
        />
        {voorstel ? (
          <div className={"ad-ai__voorstel" + (voorstel.volledig ? "" : " ad-ai__voorstel--onvolledig")}>
            <p className="ad-ai__uitleg">{voorstel.uitleg}</p>
            {/* Ook bij een onvolledig voorstel blijft staan wat er wél begrepen is: dan is te zien
                welke helft van de zin aangevuld moet worden. */}
            {!voorstel.volledig ? (
              <p className="ad-ai__hint">{t("pensionTarief.dagprijs.ai.onvolledig")}</p>
            ) : null}
          </div>
        ) : null}
        {fout ? <p className="ad-venster__fout" role="alert">{fout}</p> : null}
      </div>
    </Modal>
  );
}

export function AccommodatieDagprijzen({ vestiging }: { vestiging: VestigingKeuze }) {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";

  const [typeId, setTypeId] = useState<number | null>(null);
  // De maand linksboven; de kalender toont deze en de twee erna.
  const [eersteMaand, setEersteMaand] = useState<Date>(() => maandStart(new Date(), 0));
  const [gekozenDag, setGekozenDag] = useState<string | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  // De vlag is niet uit de browser te lezen; met de AI-functies uit bestaat de route niet en verbergt
  // het scherm de knop. Zie useAiBeschikbaar.
  const aiBeschikbaar = useAiBeschikbaar("pension-tarief/accommodaties/ai-suggestie");

  const vestigingId = vestiging.gekozenIds.size === 1 ? [...vestiging.gekozenIds][0] : null;
  const typesQuery = useCustom<AccommodatieType[]>({
    url: `pension/accommodatietypes${vestigingId == null ? "" : `?vestigingId=${vestigingId}`}`,
    method: "get",
  });
  const types = useMemo(
    () => (Array.isArray(typesQuery.data?.data) ? typesQuery.data.data : []),
    [typesQuery.data],
  );
  const gekozenType = types.find((x) => x.id === typeId) ?? types[0] ?? null;

  const van = iso(eersteMaand);
  const tot = iso(new Date(eersteMaand.getFullYear(), eersteMaand.getMonth() + 3, 0));
  const kalender = useCustom<DagprijsDto[]>({
    url: gekozenType
      ? `pension-tarief/accommodaties/${gekozenType.id}/dagprijzen?van=${van}&tot=${tot}`
      : "",
    method: "get",
    queryOptions: { enabled: !!gekozenType },
  });
  const dagen = useMemo(() => {
    const op = new Map<string, DagprijsDto>();
    for (const d of Array.isArray(kalender.data?.data) ? kalender.data.data : []) {
      op.set(d.datum, d);
    }
    return op;
  }, [kalender.data]);

  // De standaard van vandaag-in-beeld: dezelfde voor elke dag zonder eigen prijs, tenzij er in dit
  // bereik een prijswijziging valt. Dan is er niet één standaard, en dat zeggen we ook.
  const standaarden = useMemo(() => {
    const uniek = new Set<number>();
    for (const d of dagen.values()) {
      if (d.standaard != null) uniek.add(d.standaard);
    }
    return [...uniek].sort((a, b) => a - b);
  }, [dagen]);

  /** De afwijkende bedragen in beeld, oplopend — de legenda en de kleurtrap volgen deze volgorde. */
  const afwijkendeBedragen = useMemo(() => {
    const uniek = new Set<number>();
    for (const d of dagen.values()) {
      if (d.afwijkend && d.bedrag != null) uniek.add(d.bedrag);
    }
    return [...uniek].sort((a, b) => a - b);
  }, [dagen]);

  const trapVan = (bedrag: number): string => {
    const i = afwijkendeBedragen.indexOf(bedrag);
    if (i < 0) return TRAPPEN[0];
    // Bij meer bedragen dan trappen schuiven de hoogste samen; de volgorde blijft kloppen.
    const stap = Math.min(TRAPPEN.length - 1, Math.floor((i * TRAPPEN.length) / afwijkendeBedragen.length));
    return TRAPPEN[stap];
  };

  return (
    <section className="ad-dagprijzen">
      <div className="ad-dagprijzen__balk">
        <SelectField
          label={t("pensionTarief.dagprijs.accommodatie")}
          value={gekozenType ? String(gekozenType.id) : ""}
          onChange={(e) => setTypeId(Number((e.target as HTMLSelectElement).value))}
          options={types.map((x) => ({ value: String(x.id), label: x.naam }))}
        />
        <div className="ad-dagprijzen__standaard">
          <span className="ad-dagprijzen__standaard-label">{t("pensionTarief.dagprijs.standaard")}</span>
          <span className="ad-dagprijzen__standaard-waarde">
            {standaarden.length === 0
              ? t("pensionTarief.dagprijs.geenStandaard")
              : standaarden.map((b) => formatEuro(b, locale)).join(" · ")}
          </span>
          {standaarden.length > 1 ? (
            <span className="ad-dagprijzen__hint">{t("pensionTarief.dagprijs.standaardWisselt")}</span>
          ) : null}
        </div>
        {aiBeschikbaar && gekozenType ? (
          <AiHulpKnop onClick={() => setAiOpen(true)} />
        ) : null}
        <div className="ad-dagprijzen__nav">
          <Button variant="secondary" onClick={() => setEersteMaand(maandStart(eersteMaand, -3))}>
            {t("pensionTarief.dagprijs.vorige")}
          </Button>
          <Button variant="secondary" onClick={() => setEersteMaand(maandStart(new Date(), 0))}>
            {t("pensionTarief.dagprijs.vandaag")}
          </Button>
          <Button variant="secondary" onClick={() => setEersteMaand(maandStart(eersteMaand, 3))}>
            {t("pensionTarief.dagprijs.volgende")}
          </Button>
        </div>
      </div>

      {!gekozenType ? (
        <p className="ad-dagprijzen__leeg">{t("pensionTarief.dagprijs.geenTypes")}</p>
      ) : (
        <>
          <div className="ad-dagprijzen__maanden">
            {[0, 1, 2].map((n) => (
              <Maand
                key={n}
                start={maandStart(eersteMaand, n)}
                dagen={dagen}
                locale={locale}
                trapVan={trapVan}
                gekozenDag={gekozenDag}
                onKies={setGekozenDag}
                t={t}
              />
            ))}
          </div>

          <div className="ad-dagprijzen__legenda">
            <span className="ad-dagprijzen__legenda-kop">{t("pensionTarief.dagprijs.legenda")}</span>
            <span className="ad-dagprijzen__legenda-item">
              <span className="ad-dag__stip ad-dag__stip--standaard" aria-hidden="true" />
              {t("pensionTarief.dagprijs.standaard")}
              {standaarden.length === 1 ? ` ${formatEuro(standaarden[0], locale)}` : ""}
            </span>
            {afwijkendeBedragen.map((b) => (
              <span key={b} className="ad-dagprijzen__legenda-item">
                <span className={`ad-dag__stip ad-dag__stip--${trapVan(b)}`} aria-hidden="true" />
                {t("pensionTarief.dagprijs.afwijkend")} {formatEuro(b, locale)}
              </span>
            ))}
          </div>
        </>
      )}

      {aiOpen && gekozenType ? (
        <DagprijsAiVenster
          typeId={gekozenType.id}
          t={t}
          onSluit={() => setAiOpen(false)}
          onToegepast={() => { setAiOpen(false); kalender.refetch?.(); }}
        />
      ) : null}

      {gekozenDag && gekozenType ? (
        <DagVenster
          type={gekozenType}
          datum={gekozenDag}
          dag={dagen.get(gekozenDag) ?? null}
          locale={locale}
          t={t}
          onSluit={() => setGekozenDag(null)}
          onGewijzigd={() => {
            kalender.refetch?.();
            setGekozenDag(null);
          }}
        />
      ) : null}
    </section>
  );
}

/** Eén maandrooster. Maandag voorop, zoals de rest van de planning. */
function Maand({
  start, dagen, locale, trapVan, gekozenDag, onKies, t,
}: {
  start: Date;
  dagen: Map<string, DagprijsDto>;
  locale: string;
  trapVan: (bedrag: number) => string;
  gekozenDag: string | null;
  onKies: (datum: string) => void;
  t: T;
}) {
  const jaar = start.getFullYear();
  const maand = start.getMonth();
  const aantal = new Date(jaar, maand + 1, 0).getDate();
  // getDay() geeft zondag als 0; hier begint de week op maandag.
  const voorloop = (new Date(jaar, maand, 1).getDay() + 6) % 7;
  const titel = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(start);
  const kopjes = t("pensionTarief.dagprijs.weekdagen").split(",");

  return (
    <div className="ad-maand">
      <h4 className="ad-maand__kop">{titel}</h4>
      <div className="ad-maand__rooster" role="grid" aria-label={titel}>
        {kopjes.map((k) => (
          <span key={k} className="ad-maand__weekdag" role="columnheader">{k}</span>
        ))}
        {Array.from({ length: voorloop }, (_, i) => (
          <span key={`leeg-${i}`} className="ad-dag ad-dag--leeg" aria-hidden="true" />
        ))}
        {Array.from({ length: aantal }, (_, i) => {
          const datum = `${jaar}-${String(maand + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
          const d = dagen.get(datum);
          const trap = d?.afwijkend && d.bedrag != null ? trapVan(d.bedrag) : "standaard";
          return (
            <button
              key={datum}
              type="button"
              role="gridcell"
              data-testid={`dagprijs-${datum}`}
              className={
                "ad-dag ad-dag--" + trap + (gekozenDag === datum ? " ad-dag--gekozen" : "")
              }
              onClick={() => onKies(datum)}
            >
              <span className="ad-dag__nummer">{i + 1}</span>
              <span className="ad-dag__bedrag">
                {d?.bedrag == null ? "—" : formatEuro(d.bedrag, locale)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Het venster bij een aangeklikte dag: waar het verblijf over gaat, wat die dag kost, en een periode in
 * één keer. Drie dingen in één venster omdat ze samen de vraag beantwoorden "wat kost deze ruimte wanneer".
 */
function DagVenster({
  type, datum, dag, locale, t, onSluit, onGewijzigd,
}: {
  type: AccommodatieType;
  datum: string;
  dag: DagprijsDto | null;
  locale: string;
  t: T;
  onSluit: () => void;
  onGewijzigd: () => void;
}) {
  const { mutate, isLoading: bezig } = useCustomMutation();
  const [bedrag, setBedrag] = useState(dag?.bedrag != null ? String(dag.bedrag).replace(".", ",") : "");
  const [periodeVan, setPeriodeVan] = useState(datum);
  const [periodeTot, setPeriodeTot] = useState(datum);
  const [periodeBedrag, setPeriodeBedrag] = useState("");
  const [fout, setFout] = useState<string | null>(null);

  const basis = `pension-tarief/accommodaties/${type.id}/dagprijzen`;
  const getal = (v: string): number | null => {
    if (v.trim() === "") return null;
    const n = Number(v.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };

  const schrijf = (opties: { url: string; method: "put" | "delete"; values?: unknown }) => {
    setFout(null);
    mutate(
      { url: opties.url, method: opties.method, values: opties.values ?? {} },
      { onSuccess: onGewijzigd, onError: () => setFout(t("pensionTarief.dagprijs.opslaanFout")) },
    );
  };

  const dagTekst = new Intl.DateTimeFormat(locale, {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  }).format(new Date(datum + "T00:00:00"));

  return (
    <Modal open title={t("pensionTarief.dagprijs.venstertitel")} onClose={onSluit}>
      <div className="ad-venster">
        <section className="ad-venster__details">
          <div className="ad-venster__kop">
            <AccommodatieIcoon type={type} size={22} />
            <span className="ad-venster__naam">{type.naam}</span>
            {type.luxe ? <StatusBadge tone="info">{t("pensionTarief.dagprijs.luxe")}</StatusBadge> : null}
          </div>
          {type.omschrijving ? <p className="ad-venster__omschrijving">{type.omschrijving}</p> : null}
          <dl className="ad-venster__regels">
            <dt>{t("pensionTarief.dagprijs.diersoort")}</dt>
            <dd>{type.diersoort ?? "—"}</dd>
            <dt>{t("pensionTarief.dagprijs.maxDieren")}</dt>
            <dd>{type.planning?.maxDieren ?? "—"}</dd>
          </dl>
        </section>

        <section className="ad-venster__blok">
          <h4 className="ad-venster__blokkop">{t("pensionTarief.dagprijs.dagAanpassen")}</h4>
          <p className="ad-venster__dag">{dagTekst}</p>
          <Field
            label={t("pensionTarief.dagprijs.dagprijs")}
            value={bedrag}
            onChange={(e) => setBedrag((e.target as HTMLInputElement).value)}
            inputMode="decimal"
          />
          <div className="ad-venster__knoppen">
            <Button
              disabled={bezig}
              onClick={() => schrijf({
                url: `${basis}/${datum}`, method: "put", values: { bedrag: getal(bedrag) },
              })}
            >
              {t("pensionTarief.dagprijs.opslaan")}
            </Button>
            {/* "Gebruik standaard" haalt de eigen prijs weg in plaats van de standaard in te vullen:
                een ingevulde kopie schuift niet mee als de standaardprijs verandert. */}
            <Button
              variant="secondary"
              disabled={bezig || !dag?.afwijkend}
              onClick={() => schrijf({ url: `${basis}/${datum}`, method: "delete" })}
            >
              {t("pensionTarief.dagprijs.gebruikStandaard")}
            </Button>
          </div>
        </section>

        <section className="ad-venster__blok">
          <h4 className="ad-venster__blokkop">{t("pensionTarief.dagprijs.periode")}</h4>
          <div className="ad-venster__periode">
            <Field
              label={t("pensionTarief.dagprijs.van")}
              type="date"
              value={periodeVan}
              onChange={(e) => setPeriodeVan((e.target as HTMLInputElement).value)}
            />
            <Field
              label={t("pensionTarief.dagprijs.tot")}
              type="date"
              value={periodeTot}
              onChange={(e) => setPeriodeTot((e.target as HTMLInputElement).value)}
            />
            <Field
              label={t("pensionTarief.dagprijs.dagprijs")}
              value={periodeBedrag}
              onChange={(e) => setPeriodeBedrag((e.target as HTMLInputElement).value)}
              inputMode="decimal"
            />
          </div>
          <Button
            disabled={bezig || periodeVan === "" || periodeTot === ""}
            onClick={() => schrijf({
              url: basis,
              method: "put",
              values: { van: periodeVan, tot: periodeTot, bedrag: getal(periodeBedrag) },
            })}
          >
            {t("pensionTarief.dagprijs.toepassen")}
          </Button>
          <p className="ad-venster__hint">{t("pensionTarief.dagprijs.periodeHint")}</p>
        </section>

        {fout ? <p className="ad-venster__fout" role="alert">{fout}</p> : null}
      </div>
    </Modal>
  );
}
